import { gettext } from '@eventbrite/i18n';
import {
    GA_APPLE,
    GA_FB_LOGIN_EMAIL_NOT_SHARED,
    GA_FB_LOGIN_NO_EMAIL_ADDRESS,
    GA_SOCIAL_LOGIN_EMAIL_ALREADY_EXISTS,
    GA_SOCIAL_SOFT_ACCOUNT_EXISTS,
    GA_STAFF_USER_NOT_ALLOWED,
} from '../constants/analytics';
import {
    EMAIL_PERMISSION_DENIED_ERROR,
    EXTERNAL_USER_EMAIL_ALREADY_USED_ERROR,
    EXTERNAL_USER_EMAIL_USED_ON_SOFT_ACCOUNT_ERROR,
    EXTERNAL_USER_UNKNOWN_ERROR,
    EXTERNAL_USER_WITHOUT_EMAIL_ERROR,
    INVALID_SOCIAL_LOGIN_FIELD,
    STAFF_USER_NOT_ALLOWED_ERROR,
    SUSPENDED_ACCOUNT,
    TOKEN_NOT_AUTHORIZED_ERROR,
    USER_ALREADY_HAS_EXTERNAL_PROVIDER,
} from '../constants/apiErrors';
import { IAllContextsProps } from '../constants/interfaces';
import {
    DEFAULT_ERROR_MESSAGE,
    FORBIDDEN_MESSAGE,
} from '../constants/loginMessages';
import {
    EMAIL_PERMISSION_DENIED_ERROR_MESSAGE,
    EXTERNAL_USER_EMAIL_USED_ON_SOFT_ACCOUNT_ERROR_MESSAGE,
    LINK_ACCOUNTS_OPTION_FOR_EXTERNAL_USER_EMAIL_ML_MESSAGE,
    SOCIAL_ERROR_NOT_AUTHORIZED_MESSAGE,
    SOCIAL_ERROR_POPUP_BLOCKED_MESSAGE,
    SOCIAL_ERROR_SERVER_MESSAGE,
    SOCIAL_ERROR_UNKNOWN_MESSAGE,
    STAFF_USER_NOT_ALLOWED_ERROR_MESSAGE,
    TOKEN_NOT_AUTHORIZED_ERROR_MESSAGE,
    USER_ALREADY_HAS_EXTERNAL_PROVIDER_MESSAGE,
} from '../constants/messages';
import { INotificationContext } from '../contexts/NotificationsContext';
import { trackHandleOnErrorMetric } from '../tracking/login';
import { trackSocialError } from '../tracking/social';

const DEFAULT_ERROR_KEY = '__all__';

// eslint-disable complexity
// Throw especific error depending of error code.
// this is triggering redux errors, it should only throw an error message code or string
export const handleSocialLogInErrors = ({
    _errorSpec,
    responseErrorObj,
    socialProvider: _socialProvider,
    contexts,
}: {
    _errorSpec?: any;
    socialProvider?: string;
    responseErrorObj: any;
    contexts: IAllContextsProps;
}): any => {
    const errorMessage: string[] = _errorSpec[DEFAULT_ERROR_KEY];
    const { notificationsContext, userContext } = contexts;
    const analytics = contexts.configContext?.analytics;
    const socialMethods = contexts.configContext?.socialMethods;

    const setSocialData = userContext?.methods.setSocialData;
    const lookup = userContext?.methods.lookup;
    const setExistentAccount = lookup?.setExistentAccount;
    const socialData = userContext?.state.socialData;
    const socialProvider = _socialProvider ?? '';

    // this error may not be needed
    if (
        responseErrorObj.captcha &&
        responseErrorObj.captcha[0] === 'CAPTCHA_REQUIRED'
    ) {
        // throw new CaptchaError();
    } else if (errorMessage?.includes(STAFF_USER_NOT_ALLOWED_ERROR)) {
        // Scenario: social_StaffUser
        notificationsContext?.methods.setGeneralNotification({
            message: STAFF_USER_NOT_ALLOWED_ERROR_MESSAGE,
        });

        trackSocialError({
            socialProvider,
            analytics,
            action: GA_STAFF_USER_NOT_ALLOWED,
        });
    } else if (errorMessage?.includes(INVALID_SOCIAL_LOGIN_FIELD)) {
        // Scenario: social_InvalidField
        notificationsContext?.methods.setGeneralNotification({
            message: DEFAULT_ERROR_MESSAGE,
        });

        trackHandleOnErrorMetric({
            analytics,
            errorMessage: errorMessage[0],
            label: socialProvider,
        });
    } else if (errorMessage?.includes(USER_ALREADY_HAS_EXTERNAL_PROVIDER)) {
        // Scenario: social_AlreadyUsed
        notificationsContext?.methods.setGeneralNotification({
            message: USER_ALREADY_HAS_EXTERNAL_PROVIDER_MESSAGE,
        });

        trackHandleOnErrorMetric({
            analytics,
            errorMessage: errorMessage[0],
            label: socialProvider,
        });
    } else if (errorMessage?.includes(SUSPENDED_ACCOUNT)) {
        // Scenario: social_SuspendedAccount
        notificationsContext?.methods.setGeneralNotification({
            message: FORBIDDEN_MESSAGE,
        });

        trackHandleOnErrorMetric({
            analytics,
            errorMessage: errorMessage[0],
            label: socialProvider,
        });
    } else if (errorMessage?.includes(TOKEN_NOT_AUTHORIZED_ERROR)) {
        // Scenario: social_TokenNotAuthorized
        notificationsContext?.methods.setGeneralNotification({
            message: TOKEN_NOT_AUTHORIZED_ERROR_MESSAGE,
        });

        trackHandleOnErrorMetric({
            analytics,
            errorMessage: TOKEN_NOT_AUTHORIZED_ERROR,
            label: socialProvider,
        });
    } else if (errorMessage?.includes(EXTERNAL_USER_WITHOUT_EMAIL_ERROR)) {
        // Seems to be a facebook only scenario.
        // Scenario: social_EmailNotProvided
        const _data = {
            ...responseErrorObj,
            firstName:
                responseErrorObj?.firstName ?? responseErrorObj?.givenName,
            lastName:
                responseErrorObj?.lastName ?? responseErrorObj?.familyName,
        };
        if (_data?.firstName || _data?.lastName) {
            setSocialData?.({
                [socialProvider]: { ..._data, socialProvider },
            });
        }
        trackSocialError({ analytics, action: GA_FB_LOGIN_NO_EMAIL_ADDRESS });

        /**
         * It may not be having the correct data in redux, needs to be
         * triggered twice for it to work.
         * https://eventbrite.atlassian.net/browse/EB-179347
         */
        socialMethods?.error?.[EXTERNAL_USER_WITHOUT_EMAIL_ERROR]?.({
            socialProvider,
            socialData: _data,
            errorMessage,
        });
    } else if (errorMessage?.includes(EXTERNAL_USER_EMAIL_ALREADY_USED_ERROR)) {
        // Scenario: sendLinkEmailSuccess_LinkAccountSuccess
        const updatedSocialData = { ...socialData, ...responseErrorObj };
        notificationsContext?.methods.setGeneralNotification({
            message: LINK_ACCOUNTS_OPTION_FOR_EXTERNAL_USER_EMAIL_ML_MESSAGE,
        });
        trackSocialError({
            analytics,
            action: GA_SOCIAL_LOGIN_EMAIL_ALREADY_EXISTS,
            socialProvider,
        });
        if (updatedSocialData && updatedSocialData.email) {
            setExistentAccount?.(updatedSocialData.email);
        }
        if (updatedSocialData) {
            setSocialData?.({
                [socialProvider]: { ...updatedSocialData, socialProvider },
            });
        }

        socialMethods?.error?.[EXTERNAL_USER_EMAIL_ALREADY_USED_ERROR]?.({
            socialProvider,
            socialData: updatedSocialData,
            notificationMessage: LINK_ACCOUNTS_OPTION_FOR_EXTERNAL_USER_EMAIL_ML_MESSAGE,
            errorMessage,
        });
    } else if (
        errorMessage?.includes(EXTERNAL_USER_EMAIL_USED_ON_SOFT_ACCOUNT_ERROR)
    ) {
        // Scenario: social_UserIsSoftAccount
        notificationsContext?.methods.setGeneralNotification({
            message: EXTERNAL_USER_EMAIL_USED_ON_SOFT_ACCOUNT_ERROR_MESSAGE,
        });
        trackSocialError({
            analytics,
            action: GA_SOCIAL_SOFT_ACCOUNT_EXISTS,
            socialProvider,
        });
        if (
            responseErrorObj &&
            responseErrorObj.email &&
            socialProvider != GA_APPLE
        ) {
            setExistentAccount?.(responseErrorObj.email);
            setSocialData?.({
                [socialProvider]: { ...responseErrorObj, socialProvider },
            });
        }

        socialMethods?.error?.[
            EXTERNAL_USER_EMAIL_USED_ON_SOFT_ACCOUNT_ERROR
        ]?.({
            socialProvider,
            socialData: responseErrorObj,
            notificationMessage: EXTERNAL_USER_EMAIL_USED_ON_SOFT_ACCOUNT_ERROR_MESSAGE,
            errorMessage,
        });
    } else if (errorMessage?.includes(EMAIL_PERMISSION_DENIED_ERROR)) {
        // Scenario: social_EmailDenied
        // TODO Check if setReRequestPermissionStatus function is needed
        // dispatch(setReRequestPermissionStatus());

        notificationsContext?.methods.setGeneralNotification({
            message: EMAIL_PERMISSION_DENIED_ERROR_MESSAGE,
            cta: {
                message: gettext('Learn how'),
                link: 'https://www.facebook.com/help/162801153783275',
            },
        });
        trackSocialError({ analytics, action: GA_FB_LOGIN_EMAIL_NOT_SHARED });
    } else if (errorMessage?.includes(EXTERNAL_USER_UNKNOWN_ERROR)) {
        // Scenario: facebookUnknownError
        // handle special error case
        // throw new ExternalUserUnknownError();
        // TODO Check if google passes through this flow
        // let formType = SOCIAL_SIGN_UP_PATH;
        // let message = null;
        // trackFBLoginExternalUserUnknown({ state });
        // lookupUserApi(data.email).then(({ exists, can_login: canLogin }) => {
        //     // If user exists, skip confirmation view
        //     if (exists) {
        //         // If user has full account, redirect to link accounts view
        //         if (canLogin) {
        //             message = LINK_ACCOUNTS_OPTION_FOR_EXTERNAL_USER_EMAIL_ML;
        //             formType = LINK_ACCOUNTS_LOG_IN_PATH;
        //             // If user has soft account, redirect to create password view
        //         } else {
        //             message = EXTERNAL_USER_EMAIL_USED_ON_SOFT_ACCOUNT_ERROR_MESSAGE;
        //             formType = SET_LINKED_SOFT_ACCOUNTS_PATH;
        //         }
        //         dispatch(setAccountExistent(data.email));
        //         dispatch(
        //             showAuthNotification({
        //                 message,
        //                 iconType: <AlertChunky />,
        //             }),
        //         );
        //     }
        //     dispatch(setSocialDataAction(data, socialProvider));
        //     dispatch(updateFormType(formType));
        // });
        // TODO Check if these unused error cases are needed
        // } else if (errorMessage?.includes(BAD_REQUEST_ERROR)) {
        //     // handle special error case
        //     // throw new SuspendedAccountError();
        // } else if (errorMessage?.includes(INVALID_ERROR)) {
        //     // handle special error case
        //     // throw new SuspendedAccountError();
        // } else if (errorMessage?.includes(FORBIDDEN_ERROR)) {
        //     // handle special error case
        //     // throw new TokenNotAuthorizedError();
    } else {
        // throw new SubmissionError(
        //     _generateAuthenticationErrorObj(errorSpec, responseErrorObj),
        // );
    }
};

/**
 * Catch the error and shows the specific error message to the user.
 */
export const handleSocialNotAuthorizedError = ({
    error,
    notificationsContext,
}: {
    error: string;
    notificationsContext?: INotificationContext;
}) => {
    switch (error) {
        case 'popup_blocked_by_browser':
            notificationsContext?.methods.setGeneralNotification({
                message: SOCIAL_ERROR_POPUP_BLOCKED_MESSAGE,
            });
            break;

        case 'unknown': // Most common error in FB
        case 'not_defined': // No error code
            notificationsContext?.methods.setGeneralNotification({
                message: SOCIAL_ERROR_UNKNOWN_MESSAGE,
            });
            break;

        case 'access_denied':
        case 'not_authorized': // FB Error when user not authorize
        case 'user_cancelled_authorize': // On Apple, user cancel after login
            notificationsContext?.methods.setGeneralNotification({
                message: SOCIAL_ERROR_NOT_AUTHORIZED_MESSAGE,
            });
            break;

        case 'network_error': //temporal error
        case 'server_error':
            notificationsContext?.methods.setGeneralNotification({
                message: SOCIAL_ERROR_SERVER_MESSAGE,
            });
            break;
    }
};
